import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import type { AxiosResponse } from 'axios';
import { usePrivy, useSubscribeToJwtAuthWithFlag } from '@privy-io/react-auth';
import { AppAuth } from '../api/appAuth';
import i18n from '../i18n';
import {
    clearRememberedCredentials,
    clearSession,
    getAccessToken,
    getAuthSource,
    getSession,
    isSessionValid,
    parseExpiresIn,
    saveAccessToken,
    saveRememberedCredentials,
    saveSession,
    type AuthUser,
} from './storage';

type LoginResponseData = {
    success?: boolean;
    message?: string;
    accessToken?: string;
    tokenType?: string;
    expiresIn?: string;
    token?: string;
    displayName?: string;
    email?: string;
    user?: {
        id?: string;
        email?: string;
        displayName?: string;
        name?: string;
        status?: string;
        wallet?: object;
    };
};

type AuthContextValue = {
    user: AuthUser | null;
    isAuthenticated: boolean;
    authInitializing: boolean;
    profileLoading: boolean;
    login: (email: string, password: string, remember: boolean) => Promise<AxiosResponse<LoginResponseData>>;
    loginWithPrivy: () => Promise<void>;
    loginWithGoogle: () => Promise<void>;
    refreshProfile: (commit?: boolean) => Promise<AuthUser | null>;
    commitUser: (next: AuthUser) => void;
    passkeyGateOpen: boolean;
    startPasskeyGate: () => void;
    finishPasskeyGate: () => void;
    logout: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = {
    children: ReactNode;
};

const PRIVY_CUSTOM_AUTH_ENABLED =
    process.env.REACT_APP_PRIVY_CUSTOM_AUTH_ENABLED === 'true';
const PASSKEY_GATE_KEY = 'apc.passkeyGate';

function readPasskeyGate() {
    try {
        return sessionStorage.getItem(PASSKEY_GATE_KEY) === '1';
    } catch {
        return false;
    }
}

function getApiErrorMessage(error: unknown, fallback: string) {
    if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string' &&
        error.message
    ) {
        return error.message;
    }

    if (error instanceof Error) {
        return error.message;
    }

    return fallback;
}

function asRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    return value as Record<string, unknown>;
}

function toAuthUser(data: Record<string, unknown>, fallbackEmail = ''): AuthUser | null {
    const email =
        (typeof data.email === 'string' && data.email) || fallbackEmail;
    const displayName =
        (typeof data.displayName === 'string' && data.displayName) ||
        (typeof data.name === 'string' && data.name) ||
        email;

    if (!email && !displayName) {
        return null;
    }

    return {
        ...data,
        email,
        displayName,
    };
}

function parseLoginResponse(payload: LoginResponseData, fallbackEmail: string) {
    const nested = asRecord((payload as LoginResponseData & { data?: LoginResponseData }).data);
    const token =
        payload.accessToken ||
        payload.token ||
        (typeof nested?.accessToken === 'string' ? nested.accessToken : undefined) ||
        (typeof nested?.token === 'string' ? nested.token : undefined);

    if (!token) {
        throw new Error(payload.message || i18n.t('errors.loginNoToken', { ns: 'auth' }));
    }

    const apiUser = asRecord(payload.user) || asRecord(nested?.user) || {};
    const user = toAuthUser(apiUser, payload.email || fallbackEmail);

    if (!user) {
        throw new Error(i18n.t('errors.loginNoUser', { ns: 'auth' }));
    }

    return {
        token,
        expiresIn: payload.expiresIn || (typeof nested?.expiresIn === 'string' ? nested.expiresIn : '1d'),
        user,
    };
}

function unwrapProfile(payload: unknown): Record<string, unknown> | null {
    const body = asRecord(payload);
    if (!body) return null;

    const nestedUser = asRecord(body.user);
    if (nestedUser) return nestedUser;

    const nestedData = asRecord(body.data);
    if (nestedData) {
        return asRecord(nestedData.user) || nestedData;
    }

    return body;
}

function parseProfile(payload: unknown): AuthUser | null {
    const profile = unwrapProfile(payload);
    if (!profile) return null;
    return toAuthUser(profile);
}

async function fetchUserProfile(): Promise<AuthUser | null> {
    const response = await AppAuth.GetUserProfile();
    return parseProfile(response.data);
}

export function AuthProvider({ children }: AuthProviderProps) {
    const {
        ready: privyReady,
        authenticated: privyAuthenticated,
        getAccessToken: getPrivyAccessToken,
        logout: logoutPrivy,
    } = usePrivy();
    const [user, setUser] = useState<AuthUser | null>(() => getSession());
    const [authInitializing, setAuthInitializing] = useState(
        () => getAuthSource() === 'privy' && !isSessionValid()
    );
    const [profileLoading, setProfileLoading] = useState(() => isSessionValid());
    const [privySyncFailed, setPrivySyncFailed] = useState(false);
    const [passkeyGateOpen, setPasskeyGateOpen] = useState(readPasskeyGate);
    const startPasskeyGate = useCallback(() => {
        try {
            sessionStorage.setItem(PASSKEY_GATE_KEY, '1');
        } catch {
            // Ignore storage failures and keep the in-memory gate.
        }
        setPasskeyGateOpen(true);
    }, []);
    const finishPasskeyGate = useCallback(() => {
        try {
            sessionStorage.removeItem(PASSKEY_GATE_KEY);
        } catch {
            // Ignore storage failures and clear the in-memory gate.
        }
        setPasskeyGateOpen(false);
    }, []);
    const usesBackendAuth = getAuthSource() === 'backend';
    const backendAuthenticated = usesBackendAuth && Boolean(user) && isSessionValid();
    const getExternalJwt = useCallback(async () => getAccessToken() || undefined, []);
    const handlePrivySyncError = useCallback((error: Error) => {
        setPrivySyncFailed(true);
        console.error('Unable to sync APC authentication with Privy', error);
    }, []);

    useSubscribeToJwtAuthWithFlag({
        isAuthenticated: backendAuthenticated,
        isLoading: false,
        getExternalJwt,
        enabled: PRIVY_CUSTOM_AUTH_ENABLED && usesBackendAuth && !privySyncFailed,
        onError: handlePrivySyncError,
    });

    const completePrivyLogin = useCallback(
        async (
            request: (accessToken: string) => Promise<AxiosResponse<LoginResponseData>>,
            failureMessage: string
        ) => {
            const token = await getPrivyAccessToken();
            if (!token) {
                throw new Error(i18n.t('errors.privyNoToken', { ns: 'auth' }));
            }

            if (process.env.NODE_ENV === 'development') {
                console.log('[Privy JWT for backend verification]', token);
            }

            const response = await request(token);

            if (response.status !== 200 && response.status !== 201) {
                throw new Error(failureMessage);
            }

            const payload = response.data;
            if (payload?.success === false) {
                throw new Error(payload.message || failureMessage);
            }

            const {
                token: appToken,
                expiresIn,
                user: nextUser,
            } = parseLoginResponse(payload, '');

            saveAccessToken(appToken, parseExpiresIn(expiresIn), 'backend');
            saveSession(nextUser);
            setUser(nextUser);

            try {
                const profile = await fetchUserProfile();
                if (profile) {
                    saveSession(profile);
                    setUser(profile);
                }
            } catch {
                // Keep the login response user if /user/me fails.
            }
        },
        [getPrivyAccessToken]
    );

    const loginWithPrivy = useCallback(
        () =>
            completePrivyLogin(
                (accessToken) => AppAuth.UserLoginWithPrivy({ accessToken }),
                i18n.t('errors.passkey', { ns: 'auth' })
            ),
        [completePrivyLogin]
    );

    const loginWithGoogle = useCallback(
        () =>
            completePrivyLogin(
                (accessToken) => AppAuth.UserLoginWithGoogle({ accessToken }),
                i18n.t('errors.google', { ns: 'auth' })
            ),
        [completePrivyLogin]
    );

    const commitUser = useCallback((next: AuthUser) => {
        saveSession(next);
        setUser(next);
    }, []);

    const refreshProfile = useCallback(async (commit = true) => {
        try {
            const profile = await fetchUserProfile();
            if (!profile) return null;
            if (commit) {
                saveSession(profile);
                setUser(profile);
            }
            return profile;
        } catch {
            // Keep the current balance if the profile request fails.
            return null;
        }
    }, []);

    useEffect(() => {
        if (!isSessionValid()) {
            setProfileLoading(false);
            return;
        }

        let cancelled = false;
        setProfileLoading(true);

        fetchUserProfile()
            .then((profile) => {
                if (cancelled || !profile) return;
                saveSession(profile);
                setUser(profile);
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setProfileLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!privyReady) {
            return;
        }

        if (
            !privyAuthenticated ||
            getAuthSource() !== 'privy' ||
            isSessionValid()
        ) {
            setAuthInitializing(false);
            return;
        }

        setAuthInitializing(true);
        loginWithPrivy()
            .catch(() => {
                clearSession();
                setUser(null);
            })
            .finally(() => setAuthInitializing(false));
    }, [loginWithPrivy, privyAuthenticated, privyReady]);

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            isAuthenticated: Boolean(user) && isSessionValid() && !passkeyGateOpen,
            authInitializing,
            profileLoading,
            login: async (email, password, remember) => {
                const response = await AppAuth.UserLogin({
                    email: email.trim(),
                    password,
                });

                const payload = response.data as LoginResponseData;

                if (response.status !== 200 && response.status !== 201) {
                    throw new Error(
                        payload?.message || i18n.t('errors.signIn', { ns: 'auth' })
                    );
                }

                if (payload?.success === false) {
                    throw new Error(
                        payload.message || i18n.t('errors.signIn', { ns: 'auth' })
                    );
                }

                const { token, expiresIn, user: nextUser } = parseLoginResponse(
                    payload,
                    email.trim()
                );

                saveAccessToken(token, parseExpiresIn(expiresIn));
                saveSession(nextUser);
                setUser(nextUser);

                try {
                    const profile = await fetchUserProfile();
                    if (profile) {
                        saveSession(profile);
                        setUser(profile);
                    }
                } catch {
                    // Keep login user if /user/me fails.
                }

                if (remember) {
                    saveRememberedCredentials(email.trim(), password);
                } else {
                    clearRememberedCredentials();
                }

                return response;
            },
            loginWithPrivy,
            loginWithGoogle,
            refreshProfile,
            commitUser,
            passkeyGateOpen,
            startPasskeyGate,
            finishPasskeyGate,
            logout: () => {
                finishPasskeyGate();
                clearSession();
                setUser(null);
                void logoutPrivy();
            },
        }),
        [
            authInitializing,
            finishPasskeyGate,
            loginWithGoogle,
            loginWithPrivy,
            logoutPrivy,
            passkeyGateOpen,
            profileLoading,
            refreshProfile,
            commitUser,
            startPasskeyGate,
            user,
        ]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}

export { getApiErrorMessage };
